import React, { useState } from 'react';
import { Volume2, Droplets, Info } from 'lucide-react';

/**
 * Interactive DACH-region map showing regional terms for "Mineralwasser".
 * Country shapes are hand-traced simplified polygons (educational, not cartographic).
 *
 * Sources for term distribution:
 *  - Atlas zur deutschen Alltagssprache (AdA, Uni Salzburg/Liège)
 *  - Variantenwörterbuch des Deutschen (de Gruyter)
 *  - Schweizerisches Idiotikon
 */

const TERMS = {
  mineralwasser: { color: '#1d4ed8', label: 'Mineralwasser', meaning: 'Standard term across all 3 countries — works everywhere.' },
  sprudel:       { color: '#0891b2', label: 'Sprudel / Sprudelwasser', meaning: 'Sparkling water. Dominant in central & western Germany (Hesse, NRW, RLP, BW).' },
  selters:       { color: '#3b82f6', label: 'Selters',              meaning: 'Genericized from the brand "Selters" (Hesse). Used as a synonym for Sprudel in parts of Hesse, Saarland, Luxembourg-border areas.' },
  sauer:         { color: '#0284c7', label: 'Sauerwasser',          meaning: 'Eifel region & parts of Saarland — refers to naturally carbonated mineral springs ("sauer" = acidic from CO₂).' },
  soda:          { color: '#dc2626', label: 'Soda / Sodawasser',    meaning: 'Austria-wide (esp. Vienna & east). Soda = plain carbonated water; "Soda-Zitron" is the classic.' },
  prickelwasser: { color: '#ea580c', label: 'Prickelwasser',        meaning: 'Colloquial Austrian — "the tingling water".' },
  blaeterli:     { color: '#16a34a', label: 'Mit Bläterli',         meaning: 'Swiss German colloquial — "with little bubbles". "Ohni Bläterli" = still water.' },
  hahnenwasser:  { color: '#0d9488', label: 'Hahne(n)wasser',       meaning: 'Swiss German for TAP water (free in some cantons, charged in others). Not mineral water — but often confused.' },
  gazeuse:       { color: '#db2777', label: "L'eau gazeuse",        meaning: 'French-speaking Switzerland (Geneva, Lausanne, Neuchâtel). "Plate" = still.' },
  frizzante:     { color: '#0e7490', label: 'Acqua frizzante',      meaning: 'Italian-speaking Switzerland (Ticino). "Naturale" = still.' },
};

// City marker = { x, y, name, country, terms: [keys of TERMS] }
const CITIES = [
  // Germany
  { x: 324, y: 148, name: 'Hamburg',     country: 'DE', terms: ['mineralwasser'] },
  { x: 470, y: 175, name: 'Berlin',      country: 'DE', terms: ['mineralwasser'] },
  { x: 155, y: 290, name: 'Köln',        country: 'DE', terms: ['sprudel', 'mineralwasser'] },
  { x: 130, y: 320, name: 'Eifel',       country: 'DE', terms: ['sauer', 'sprudel'] },
  { x: 245, y: 350, name: 'Frankfurt',   country: 'DE', terms: ['sprudel', 'selters'] },
  { x: 195, y: 370, name: 'Wiesbaden',   country: 'DE', terms: ['selters', 'sprudel'] },
  { x: 275, y: 425, name: 'Stuttgart',   country: 'DE', terms: ['sprudel', 'mineralwasser'] },
  { x: 405, y: 470, name: 'München',     country: 'DE', terms: ['mineralwasser', 'sprudel'] },
  { x: 425, y: 380, name: 'Nürnberg',    country: 'DE', terms: ['sprudel', 'mineralwasser'] },
  { x: 480, y: 290, name: 'Leipzig',     country: 'DE', terms: ['mineralwasser'] },

  // Austria
  { x: 700, y: 498, name: 'Wien',        country: 'AT', terms: ['soda', 'prickelwasser', 'mineralwasser'] },
  { x: 510, y: 519, name: 'Salzburg',    country: 'AT', terms: ['soda', 'mineralwasser'] },
  { x: 555, y: 525, name: 'Linz',        country: 'AT', terms: ['soda', 'mineralwasser'] },
  { x: 445, y: 540, name: 'Innsbruck',   country: 'AT', terms: ['mineralwasser', 'soda'] },
  { x: 625, y: 540, name: 'Graz',        country: 'AT', terms: ['soda', 'mineralwasser'] },

  // Switzerland
  { x: 232, y: 575, name: 'Zürich',      country: 'CH', terms: ['blaeterli', 'mineralwasser'] },
  { x: 166, y: 595, name: 'Bern',        country: 'CH', terms: ['blaeterli', 'mineralwasser', 'hahnenwasser'] },
  { x: 187, y: 575, name: 'Basel',       country: 'CH', terms: ['blaeterli', 'mineralwasser'] },
  { x: 105, y: 625, name: 'Genève',      country: 'CH', terms: ['gazeuse'] },
  { x: 140, y: 615, name: 'Lausanne',    country: 'CH', terms: ['gazeuse'] },
  { x: 270, y: 640, name: 'Lugano',      country: 'CH', terms: ['frizzante'] },
];

const MineralwasserMap = () => {
  const [activeCity, setActiveCity] = useState(null);
  const [hoveredTerm, setHoveredTerm] = useState(null);

  const speak = (text, lang = 'de-DE') => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      u.rate = 0.9;
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8" data-testid="mineralwasser-map">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-3 py-1.5 rounded-full text-xs font-semibold mb-3">
          <Droplets className="w-3.5 h-3.5" />
          Regional Language Atlas
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
          How to Order Sparkling Water in the DACH Region
        </h3>
        <p className="text-babyblue-600 max-w-2xl mx-auto">
          The same drink has different names in Germany, Austria & Switzerland. Click a city to hear how locals say it.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* MAP */}
        <div className="relative bg-gradient-to-b from-babyblue-50 to-white rounded-xl border border-babyblue-200 overflow-hidden">
          <svg viewBox="0 0 820 720" className="w-full h-auto" role="img" aria-label="Map of Germany, Austria and Switzerland">
            {/* Background grid */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#dbeafe" strokeWidth="0.5" />
              </pattern>
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
              </filter>
            </defs>
            <rect width="820" height="720" fill="url(#grid)" />

            {/* GERMANY */}
            <path
              d="M 130 60 Q 200 45 280 50 Q 360 55 430 65 Q 490 85 515 125 Q 540 175 555 230 Q 565 280 540 305 Q 510 320 495 350 Q 480 385 470 420 Q 462 460 455 495 Q 430 510 395 505 Q 350 500 305 495 Q 270 490 240 480 Q 210 460 185 430 Q 160 395 140 355 Q 120 305 108 255 Q 100 205 102 160 Q 110 100 130 60 Z"
              fill="#dbeafe"
              fillOpacity="0.55"
              stroke="#1d4ed8"
              strokeWidth="2.5"
              filter="url(#shadow)"
            />
            <text x="280" y="155" fontSize="26" fontWeight="800" fill="#1e3a8a" opacity="0.25" textAnchor="middle" letterSpacing="2">DEUTSCHLAND</text>

            {/* AUSTRIA */}
            <path
              d="M 340 510 L 410 503 L 490 500 L 560 498 L 620 495 L 680 490 L 725 488 L 740 502 L 745 522 L 730 545 L 695 560 L 640 565 L 580 562 L 525 568 L 475 562 L 425 552 L 380 542 L 348 528 L 340 510 Z"
              fill="#fee2e2"
              fillOpacity="0.55"
              stroke="#dc2626"
              strokeWidth="2.5"
              filter="url(#shadow)"
            />
            <text x="595" y="530" fontSize="18" fontWeight="800" fill="#991b1b" opacity="0.3" textAnchor="middle" letterSpacing="1.5">ÖSTERREICH</text>

            {/* SWITZERLAND */}
            <path
              d="M 75 580 Q 110 545 165 540 Q 215 538 265 545 Q 305 555 320 575 Q 325 605 295 625 Q 250 650 195 648 Q 135 642 95 620 Q 70 605 75 580 Z"
              fill="#dcfce7"
              fillOpacity="0.55"
              stroke="#16a34a"
              strokeWidth="2.5"
              filter="url(#shadow)"
            />
            <text x="195" y="660" fontSize="16" fontWeight="800" fill="#14532d" opacity="0.3" textAnchor="middle" letterSpacing="1.5">SCHWEIZ</text>

            {/* Region highlights when hovering term */}
            {hoveredTerm === 'sauer' && (
              <circle cx="135" cy="320" r="40" fill="#0284c7" opacity="0.2" stroke="#0284c7" strokeWidth="2" strokeDasharray="4,4" />
            )}
            {hoveredTerm === 'selters' && (
              <ellipse cx="215" cy="370" rx="55" ry="35" fill="#3b82f6" opacity="0.18" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4,4" />
            )}
            {hoveredTerm === 'gazeuse' && (
              <ellipse cx="120" cy="620" rx="50" ry="22" fill="#db2777" opacity="0.22" stroke="#db2777" strokeWidth="2" strokeDasharray="4,4" />
            )}
            {hoveredTerm === 'frizzante' && (
              <circle cx="270" cy="640" r="22" fill="#0e7490" opacity="0.22" stroke="#0e7490" strokeWidth="2" strokeDasharray="4,4" />
            )}

            {/* CITY DOTS - rendered as raw SVG to avoid dev-tool span injection inside SVG */}
            <g
              onClick={(e) => {
                const target = e.target.closest('[data-city-name]');
                if (target) {
                  const name = target.getAttribute('data-city-name');
                  const city = CITIES.find(c => c.name === name);
                  if (city) setActiveCity(city);
                }
              }}
              // Safe: this SVG markup is built exclusively from the static CITIES & TERMS
              // constants defined in this file. No user-controlled input is interpolated.
              dangerouslySetInnerHTML={{
                __html: CITIES.map((city) => {
                  const isActive = activeCity?.name === city.name;
                  const isDimmed = hoveredTerm && !city.terms.includes(hoveredTerm);
                  const color = TERMS[city.terms[0]].color;
                  const r = isActive ? 18 : 15;
                  const pulseR = isActive ? 32 : 26;
                  const pulse = city.terms.length > 1
                    ? `<circle cx="${city.x}" cy="${city.y}" r="${pulseR}" fill="${color}" opacity="0.25" />`
                    : '';
                  return `
                    <g data-city-name="${city.name}" data-testid="map-city-${city.name.toLowerCase()}" style="cursor:pointer; opacity:${isDimmed ? 0.25 : 1}; transition:opacity 200ms;">
                      ${pulse}
                      <circle cx="${city.x}" cy="${city.y}" r="${r}" fill="${color}" stroke="#fff" stroke-width="4" />
                      <text x="${city.x}" y="${city.y - 24}" font-size="20" font-weight="800" fill="#0f172a" text-anchor="middle" stroke="#fff" stroke-width="5" stroke-linejoin="round" paint-order="stroke" style="pointer-events:none;">${city.name}</text>
                    </g>
                  `;
                }).join('')
              }}
            />
          </svg>
        </div>

        {/* LEGEND + DETAILS */}
        <div className="space-y-4">
          {/* Active City Detail */}
          {activeCity ? (
            <div className="bg-babyblue-50 border-2 border-primary rounded-xl p-5" data-testid="active-city-detail">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-lg text-babyblue-900">{activeCity.name}</h4>
                <span className="text-xs font-semibold bg-white px-2 py-1 rounded-full text-babyblue-700 border border-babyblue-200">
                  {activeCity.country === 'DE' ? '🇩🇪 Germany' : activeCity.country === 'AT' ? '🇦🇹 Austria' : '🇨🇭 Switzerland'}
                </span>
              </div>
              <p className="text-xs text-babyblue-600 mb-3 font-medium uppercase tracking-wider">Locals say:</p>
              <div className="space-y-2">
                {activeCity.terms.map((termKey) => {
                  const term = TERMS[termKey];
                  return (
                    <button
                      key={termKey}
                      onClick={() => speak(term.label, termKey === 'gazeuse' ? 'fr-FR' : termKey === 'frizzante' ? 'it-IT' : 'de-DE')}
                      className="w-full flex items-center gap-3 bg-white p-3 rounded-lg border border-babyblue-200 hover:border-primary hover:shadow-sm transition-all group text-left"
                      data-testid={`speak-${termKey}`}
                    >
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: term.color }}></span>
                      <span className="font-semibold text-babyblue-800 flex-1">{term.label}</span>
                      <Volume2 className="w-4 h-4 text-primary opacity-60 group-hover:opacity-100" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-babyblue-50 border-2 border-dashed border-babyblue-300 rounded-xl p-5 text-center">
              <Info className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-sm text-babyblue-700 font-medium">Click any city on the map to hear the local term.</p>
            </div>
          )}

          {/* Term Legend */}
          <div>
            <p className="text-xs font-semibold text-babyblue-500 uppercase tracking-wider mb-2">All Terms</p>
            <div className="space-y-1.5">
              {Object.entries(TERMS).map(([key, t]) => (
                <button
                  key={key}
                  onMouseEnter={() => setHoveredTerm(key)}
                  onMouseLeave={() => setHoveredTerm(null)}
                  onFocus={() => setHoveredTerm(key)}
                  onBlur={() => setHoveredTerm(null)}
                  onClick={() => {
                    setHoveredTerm(key);
                    speak(t.label, key === 'gazeuse' ? 'fr-FR' : key === 'frizzante' ? 'it-IT' : 'de-DE');
                  }}
                  className="w-full flex items-start gap-2 p-2 rounded-lg hover:bg-babyblue-50 transition-colors text-left"
                  data-testid={`legend-${key}`}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ background: t.color }}></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-babyblue-800">{t.label}</p>
                    <p className="text-xs text-babyblue-600 leading-snug">{t.meaning}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Reference Table */}
      <div className="mt-6 bg-babyblue-50 rounded-xl p-5 border border-babyblue-200">
        <p className="text-xs font-semibold text-babyblue-500 uppercase tracking-wider mb-3">Restaurant Cheat Sheet</p>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-bold text-blue-700 mb-1">🇩🇪 In Germany, order:</p>
            <p className="text-babyblue-700"><strong>"Ein Sprudel, bitte"</strong> (south/west) or <strong>"Ein Mineralwasser mit Kohlensäure"</strong> (anywhere)</p>
          </div>
          <div>
            <p className="font-bold text-red-700 mb-1">🇦🇹 In Austria, order:</p>
            <p className="text-babyblue-700"><strong>"Ein Soda, bitte"</strong> — or <strong>"Soda-Zitron"</strong> for soda with lemon syrup (the Austrian classic)</p>
          </div>
          <div>
            <p className="font-bold text-green-700 mb-1">🇨🇭 In Switzerland, order:</p>
            <p className="text-babyblue-700"><strong>"Mit Bläterli"</strong> (German CH), <strong>"Gazeuse"</strong> (French CH), <strong>"Frizzante"</strong> (Ticino)</p>
          </div>
        </div>
      </div>

      <p className="text-xs text-babyblue-500 italic mt-4 text-center">
        Sources: Atlas zur deutschen Alltagssprache (AdA), Variantenwörterbuch des Deutschen, Schweizerisches Idiotikon. Regional usage overlaps — most cities use multiple terms.
      </p>
    </div>
  );
};

export default MineralwasserMap;
